// @ts-strict-ignore
/* eslint-disable max-len */
import React, {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route, Navigate, useLocation, useMatch, useParams,
  matchPath,
  Outlet,
} from 'react-router-dom';
import Helmet from 'react-helmet';
import find from 'lodash/find';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import ErrorBoundary from '~/components/ErrorBoundary';
import Messages from '~/dashboard/components/Messages';
import SessionManagement from '~/dashboard/components/SessionManagement';
import Sidebar from '~/dashboard/components/Sidebar/index';
import { SideNav } from '~/dashboard/components/Sidebar_v2';
import Topbar from '~/dashboard/components/Topbar';
import { ResetPasswordViewMode, SPA_PREFIX, ValidateEmailMode } from '~/dashboard/constants';
import { AuthContext, MessageContext, UserContext } from '~/dashboard/context';
import { useExpandingSidebar } from './hooks';
import { useTitleContext } from './PageTitleProvider';
import { routes } from '~/helpers/routes';
import { useAppSelector } from '~/store/reduxHooks';
import { setPrintMode as _setPrintMode } from '~/redux/printMode';
import favicon from './favicon.png';

// NOT LAZY LOADED ROUTES
import ErrorStatusView from '~/dashboard/views/error-status';
import LoadingView from '~/dashboard/views/LoadingView';
import NoMatchView from '~/dashboard/views/NoMatchView';
import AuthContainerView from './views/authentication/views/AuthContainerView';
import SigninView from './views/authentication/views/SigninView';
import { parse } from 'query-string';
import MFASetupView, { MfaSetupViewWithTotp } from './views/authentication/views/MFASetupView';
import ForgotPasswordView from './views/authentication/views/ForgotPasswordView';
import ResetPasswordView from './views/authentication/views/ResetPasswordView';
import TotpVerifyView from './views/authentication/views/TotpVerifyView';
import VerifyEmailView from './views/authentication/views/VerifyEmailView';
import { Banner, useSnackBar } from '@usealloy/component-library';
import PageSpecificBanner from '~/components/PageSpecificBanner';
import { EvaluationsTabs } from '~/evaluations/context/constants';
import { PortfolioEvaluationView } from '~/portfolio-evaluations/PortfolioEvaluationView';
import PostLoginCloseView from './views/authentication/views/PostLoginCloseView';
import { LibraryRouteGuard } from './admin/library/global-repository/LibraryRouteGuard/LibraryRouteGuard';
import { RerunEvaluationView } from './views/evaluation/RerunEvaluationView';
import FDMigrationGate from './frontend-dashboard-rollout/FDMigrationGate';

function parseCookies() {
  return document.cookie.split(';').reduce((cookies, cookie) => {
    const [name, value] = cookie.trim().split('=');
    cookies[name] = value ? decodeURIComponent(value) : '';
    return cookies;
  }, {});
}

// SUB ROUTERS
const JourneyRoutes = React.lazy(() => import('~/journey/JourneyRoutes'));

// FUTURE: Copy lazy load util from https://github.com/JLarky/react-lazily/blob/main/src/core/lazily.ts

// LAZY LOAD ADMIN ROUTES
const CustomersDetailView = React.lazy(() => import('./views/admin/customers/detail'));
const DecryptLogsView = React.lazy(() => import('~/dashboard/views/admin/DecryptLogsView'));
const DecryptLogsViewOld = React.lazy(() => import('~/dashboard/views/admin/DecryptLogsViewOld'));
const EmployeesIndex = React.lazy(() => import('~/dashboard/admin/employees'));
const AiIndex = React.lazy(() => import('~/admin/customer/ai'));
const Metrics = React.lazy(() => import('~/dashboard/admin/metrics'));
const NotificationListView = React.lazy(() => import('./views/admin/NotificationList'));
const ReindexView = React.lazy(() => import('~/dashboard/views/admin/ReindexView'));
const RequestAccessView = React.lazy(() => import('~/dashboard/admin/request-access'));
const CustomerAccessRequestDetails = React.lazy(() => import('./admin/customer-access'));
const CustomerListViewProvisioning = React.lazy(() => import('./admin/provisioning'));
const PortfolioEvaluation = React.lazy(() => import('~/portfolio-evaluations/PortfolioEvaluation'));
const ServiceAttributeCreationOverlay = React.lazy(() => import('./admin/services').then(module => ({ default: module.ServiceAttributeCreationOverlay })));
const ServiceAttributeDetailsView = React.lazy(() => import('./admin/services').then(module => ({ default: module.ServiceAttributeDetailsView })));
const ServiceDetail = React.lazy(() => import('./admin/services').then(module => ({ default: module.ServiceDetail })));
const Services = React.lazy(() => import('./admin/services').then(module => ({ default: module.Services })));
const GlobalRepository = React.lazy(() => import('./admin/library/global-repository'));
const GlobalSnippetDetailsView = React.lazy(() => import('./admin/library/global-repository/SnippetVersionView/GlobalSnippetDetailsView').then(module => ({ default: module.GlobalSnippetDetailsView })));

// LAZY LOAD ALL OTHER ROUTES
const HomeDashboard = React.lazy(() => import('~/analytics/HomeDashboard'));
const AuditView = React.lazy(() => import('~/dashboard/views/AuditView'));
const CasesView = React.lazy(() => import('~/dashboard/views/CasesView').then(module => ({ default: module.CasesView })));
const CaseView = React.lazy(() => import('~/dashboard/views/CaseView'));
const EntitiesListView = React.lazy(() => import('~/dashboard/views/entities/EntitiesListView').then(module => ({ default: module.EntitiesListView })));
const EntityReviewView = React.lazy(() => import('~/dashboard/views/entities/EntityReviewView'));
const NotificationsView = React.lazy(() => import('~/dashboard/views/notifications'));
const SettingsView = React.lazy(() => import('./views/SettingsView'));
const EntityDetailView = React.lazy(() => import('~/views/EntityDetailView'));
const ReviewQueueView = React.lazy(() => import('~/views/ReviewQueueView'));
const ApprovalTableView = React.lazy(() => import('./views/ApprovalTableView'));
const EntityEventView = React.lazy(() => import('./views/entities/EntityEventView'));
const EvaluationListView = React.lazy(() => import('./views/evaluation/EvaluationListView'));
const EvaluationRunViewWrapper = React.lazy(() => import('./views/evaluation/EvaluationRunView'));
const GroupDetailView = React.lazy(() => import('./views/group/GroupDetailView'));
const InvestigationsQueueView = React.lazy(() => import('./views/investigations/InvestigationsQueueView'));
const InvestigationDetailView = React.lazy(() => import('./views/investigations/InvestigationDetailView'));
const SARForm = React.lazy(() => import('~/dashboard/views/sars/SARForm'));
const CTRListView = React.lazy(() => import('./views/ctrs/CTRListView').then(module => ({ default: module.CTRListView })));
const CTRFormView = React.lazy(() => import('./views/ctrs/CTRFormView').then(module => ({ default: module.CTRFormView })));
const SARListView = React.lazy(() => import('./views/sars/SARListView').then(module => ({ default: module.SARListView })));
const WorkflowListContainer = React.lazy(() => import('~/workflow-list/WorkflowListContainer'));
const LocalSnippetDetailsView = React.lazy(() => import('~/workflow-list/LocalSnippetDetailsView').then(module => ({ default: module.LocalSnippetDetailsView })));
const WorkflowDetailsView = React.lazy(() => import('~/workflow/WorkflowDetailsView'));
const WorkflowEditorView = React.lazy(() => import('~/dashboard/views/workflows'));
const WhatIfEvalutionView = React.lazy(() => import('../flow/what-if/EvaluationView'));
const TestHistoryResultsPage = React.lazy(() => import('../workflow/workflowTestHistory/TestHistoryResultsPage'));
const AIOverviewView = React.lazy(() => import('~/dashboard/views/ai-overview/AIOverviewView'));
const AgentDetailView = React.lazy(() => import('~/dashboard/views/ai-overview/AgentDetailView'));
// AI-testing pages — part of the AI section (gated on enableAiOverview), reached from the
// KYC Investigation card on the AI Overview page.
const AiTestingResultsPage = React.lazy(() => import('../aiTesting/AiTestingResultsPage'));
const AiTestingBacktestSetup = React.lazy(() => import('../aiTesting/AiTestingBacktestSetup'));
const PartnerCenterView = React.lazy(() => import('~/dashboard/views/partner-solutions/PartnerCenterView'));
const IntegrationView = React.lazy(() => import('~/dashboard/views/partner-solutions/IntegrationView'));
const AttributeCreation = React.lazy(() => import('~/dashboard/views/partner-solutions/integrationTabs/attribute/attributeCreation/AttributeCreation'));
const CustomerAttributeDetailsView = React.lazy(() => import('~/dashboard/views/partner-solutions/integrationTabs/attribute/CustomerAttributeDetails'));
const PolicyEditorView = React.lazy(() => import('~/dashboard/views/journeys/PolicyEditorView'));
const RuleExplainabilityView = React.lazy(() => import('~/dashboard/views/workflows/editor/components/RuleExplainability/RuleExplainabilityView'));

const SpaAdminPassThrough = ({ isEmployee }: { isEmployee: boolean }) => (isEmployee ? <Outlet /> : <Navigate replace to={`${SPA_PREFIX}/settings`} />);

const RouteWithServiceParamWrapper = ({ currentRoute }: { currentRoute: string }) => {
  const match = useMatch(currentRoute);

  if (!match) {
    return <NoMatchView />;
  }

  const { params } = match;

  if (!params?.service) {
    return <NoMatchView />;
  }

  const { service } = params;

  return <Navigate replace to={`${SPA_PREFIX}/admin/global-services/${service || ''}`} />;
};

// Back-compat: the admin library route was renamed from
// /admin/library/global-repository -> /admin/library/snippets (BUILD-4749).
// Redirect any lingering links (sub-paths, query strings, and hashes included) to the new URL.
export const RedirectGlobalRepositoryToSnippets = () => {
  const { '*': splat } = useParams();
  const { search, hash } = useLocation();
  const base = `${SPA_PREFIX}/admin/library/snippets`;
  const target = splat ? `${base}/${splat}` : base;
  return <Navigate replace to={`${target}${search}${hash}`} />;
};

const PublicOnlyLayout = ({ isAuthenticated, redirectToVanity, redirectToAlloyCom }) => {
  const addSnackbar = useSnackBar();
  const location = useLocation();
  // Migrated logic from AuthenticationRoute
  const urlParams = useMemo(() => (parse(location.search)), [location]);
  const authRoutes = useMemo(() => ([
    {
      id: 'login',
      path: `${SPA_PREFIX}/login`,
      resetAuth: false,
      redirectHome: true,
      exact: false,
      component: <SigninView />,
    },
    {
      id: 'mfa-setup-totp',
      path: `${SPA_PREFIX}/mfa/totp/verify/:tokenUid/:token`,
      redirectHome: false,
      exact: true,
      component: <MfaSetupViewWithTotp />,
    },
    {
      id: 'login-admin',
      path: `${SPA_PREFIX}/login/admin`,
      resetAuth: false,
      redirectHome: true,
      exact: false,
      component: <SigninView />,
    },
    {
      id: 'forgot-password',
      path: `${SPA_PREFIX}/forgot-password`,
      resetAuth: true,
      redirectHome: false,
      exact: false,
      component: <ForgotPasswordView />,
    },
    {
      id: 'reset-password',
      path: `${SPA_PREFIX}/reset-password/:tokenUid/:token`,
      resetAuth: true,
      redirectHome: false,
      exact: false,
      component: <ResetPasswordView mode={ResetPasswordViewMode.RESETPW} />,
    },
    {
      id: 'agent-invitation-set-password',
      path: `${SPA_PREFIX}/invitation/:tokenUid/:token`,
      resetAuth: true,
      redirectHome: false,
      exact: false,
      component: <ResetPasswordView mode={ResetPasswordViewMode.SETPW} />,
    },
    {
      id: 'mfa-totp-verify-code',
      path: `${SPA_PREFIX}/mfa/totp/:tokenUid/:token`,
      resetAuth: false,
      redirectHome: false,
      exact: false,
      component: <TotpVerifyView />,
    },
    {
      id: 'agent-verify-email-change',
      path: `${SPA_PREFIX}/verify-email/:tokenUid/:token`,
      resetAuth: true,
      redirectHome: false,
      exact: false,
      component: <VerifyEmailView mode={ValidateEmailMode.VALIDATEEMAIL} />,
    },
    {
      id: 'agent-verify-email-multiple-customer',
      path: `${SPA_PREFIX}/verify-customer-email/:tokenUid/:tokenCid/:token`,
      resetAuth: true,
      redirectHome: false,
      exact: false,
      component: <VerifyEmailView mode={ValidateEmailMode.VALIDATECUSTOMEREMAIL} />,
    },
    {
      id: 'mfa-setup-verify-phonenumber',
      path: `${SPA_PREFIX}/mfa/verify/:tokenUid/:token`,
      resetAuth: false,
      redirectHome: false,
      exact: false,
      component: <MFASetupView />,
    },
    {
      id: 'mfa-verify-code',
      path: `${SPA_PREFIX}/mfa/:tokenUid/:token`,
      resetAuth: false,
      redirectHome: false,
      exact: false,
      component: <TotpVerifyView />,
    },
  ]), []);

  const authRouteShouldResetAuth = useMemo(() => !!(
    authRoutes.find(route => matchPath(route.path, location.pathname) && route.resetAuth)), [authRoutes, location]);

  const authRouteShouldRedirectHome = useMemo(() => !!(
    authRoutes.find(route => matchPath(route.path, location.pathname) && route.redirectHome)), [authRoutes, location]);

  useEffect(() => {
    if (authRouteShouldResetAuth && isAuthenticated) {
      // this means user is in one of the auth routes that is set to reset auth state
      window.location.replace('/logout/');
    }
    if (authRouteShouldRedirectHome && isAuthenticated) {
      // this means user is in one of the auth routes that is set to redirect home state
      window.location.replace(routes.v3.dashboard.home.dashboard);
    }
  }, [authRouteShouldResetAuth, authRouteShouldRedirectHome, isAuthenticated]);

  const isUserOnAuthRoute = useMemo(() => {
    const authRouteMatches = authRoutes.filter(route => (
      matchPath(route.path, location.pathname)
    ));

    return authRouteMatches?.length > 0;
  }, [authRoutes, location]);

  const loginRequiredMsg = useMemo(() => {
    let msg = 'Please log in to access the page.';
    if (urlParams?.result === 'logout_success') {
      if (urlParams?.reason === 'ip_denied') {
        msg = 'Access was denied because your IP address was not found in the organization IP whitelist. Please contact your organization administrator.';
      } else {
        msg = `You have been logged out. ${msg}`;
      }
    }

    return msg;
  }, [urlParams]);

  useEffect(() => {
    if (isAuthenticated === false && !isUserOnAuthRoute && location.state?.from?.pathname) {
      const currentPathname = location.state?.from?.pathname;

      window.location.href = `${routes.v3.dashboard.authentication.login}/?next=${currentPathname}`;
    }
  }, [isAuthenticated, location, isUserOnAuthRoute]);

  useEffect(() => {
    if (isAuthenticated === false
      && urlParams?.next
      && sessionStorage.getItem('oneTimeTotpSuccess') === 'true'
    ) {
      addSnackbar({
        title: loginRequiredMsg,
        type: 'danger',
      });
    }
  }, [isAuthenticated, addSnackbar, urlParams, loginRequiredMsg]);

  if (isAuthenticated) {
    // Redirect to home/dashboard or the page they were trying to access before login
    const fromLocation = location.state?.from;
    const destination = fromLocation
      ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
      : `${SPA_PREFIX}/home`;
    if (
      urlParams?.next
      && !location.state?.from?.pathname
      && redirectToVanity
      && redirectToAlloyCom
    ) {
      // Construct full URL with current domain and the next parameter path
      const currentOrigin = window.location.origin;
      const nextPath = typeof urlParams.next === 'string' ? urlParams.next : '';
      setTimeout(() => {
        window.location.href = `${currentOrigin}${nextPath}`;
      }, 0);
      return null;
    }

    return <Navigate to={destination} replace />;
  }

  // Outlet renders child routes when not authenticated
  // todo - figure out how to rollout beta versions of public routes
  return <Outlet />;
};

// eslint-disable-next-line react/prop-types
PublicOnlyLayout.propTypes = {
  isAuthenticated: PropTypes.bool,
  redirectToVanity: PropTypes.bool,
  redirectToAlloyCom: PropTypes.bool,
};

const ProtectedLayout = ({ isAuthenticated, sessionExpireAfter, organizationName }) => {
  const location = useLocation();
  const addSnackbar = useSnackBar();
  const {
    agent: { email },
    isInformationalView,
    featureFlags,
    betaCookies,
  } = useContext(UserContext);
  // const email = agent ? agent.email : null;
  const {
    sidebarExpanded, toggleSidebarExpanded, isOpenMobile, onOpenMobileChange,
  } = useExpandingSidebar();
  const isTestingSuiteVisible = useAppSelector(state => (
    state.workflowTestingSuite.shared.isVisible
  ));
  const { deleteMessage, displayedAt, messages } = useContext(MessageContext);
  const isSideNavEnabled = Boolean(featureFlags?.fepSidenav) && Boolean(betaCookies?.fepSidenav);

  const [loading, setLoading] = useState<boolean | string>(true);

  // When agent has defined customerId, we've loaded necessary state
  useEffect(() => { if (email !== undefined) setLoading(false); }, [email]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const currentUrl = new URL(window.location.href);
      const currentPath = currentUrl.pathname;
      if (document.visibilityState === 'visible') {
        const cookies = parseCookies();
        const currentDomain = window.location.hostname;
        const shouldSetCookie = (!('lastDomain' in cookies) || cookies.lastDomain !== currentDomain)
          && currentDomain.endsWith('app.alloy.com');
        if (shouldSetCookie) {
          document.cookie = `lastDomain=${currentDomain}; domain=.app.alloy.com; path=/;`;
          if (!currentPath.includes('/v3/dashboard/admin')) {
            addSnackbar({
              title: `Caution: You are now viewing ${organizationName || currentDomain}`,
              type: 'warning',
            });
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function to remove the event listener when the component unmounts
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [addSnackbar, organizationName]);

  if (!isAuthenticated) {
    // Redirect to login, preserving the intended destination
    const nextFullPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    const destination = `${SPA_PREFIX}/login${nextFullPath ? `?next=${encodeURIComponent(nextFullPath)}` : ''}`;
    return <Navigate to={destination} state={{ from: location }} replace />;
  }

  return (
    <div className="dashboard-view">
      <FDMigrationGate>
        {/* Agents get the FE2.0-style nav only when their org has the `fepSidenav` LaunchDarkly
          flag enabled AND they haven't opted out via the httponly `alloy-enable-fep-sidenav`
          cookie (surfaced by the server as `betaCookies.fepSidenav`). The cookie defaults to
          "on", so it acts as an opt-out rather than an opt-in. */}
        {isSideNavEnabled ? (
          <SideNav
            expanded={!!sidebarExpanded}
            isOpenMobile={isOpenMobile}
            onExpandedChange={toggleSidebarExpanded}
            onOpenMobileChange={onOpenMobileChange}
          />
        ) : (
          <Sidebar
            expanded={!!sidebarExpanded}
            toggleExpanded={toggleSidebarExpanded}
            {...(Boolean(featureFlags.fepSidenav) && Boolean(featureFlags?.fepSidenavDefaultOff)
            && !location.pathname.includes('/settings/beta-features')
            && { 'data-pendo-anchor': 'beta-sidenav-default-off' })}
          />
        )}
        <SessionManagement sessionExpireAfter={sessionExpireAfter} />
        <div className={`container-fluid flex flex-col space ${sidebarExpanded ? '' : 'expanded'}`}>
          <div className="container-top" style={isTestingSuiteVisible ? { overflow: 'hidden' } : {}}>
            <PageSpecificBanner />
            {isInformationalView && (
            <Banner open variant="testing" style={{ borderRadius: 0 }}>
              {'You\'re looking at test data. All results and actions will not reflect in your production data.'}
            </Banner>
            )}
            <Topbar
              isOpenMobile={isOpenMobile}
              onOpenMobileChange={onOpenMobileChange}
            />

            <Messages
              sidebarExpanded={!!sidebarExpanded}
              messages={messages}
              displayedAt={displayedAt}
              deleteMessage={deleteMessage}
            />

            <ErrorBoundary>
            <Suspense fallback={<LoadingView />}> {/* eslint-disable-line react/jsx-indent -- Temporary to reduce git diff */}
              {loading ? <LoadingView /> : <Outlet />}
            </Suspense>
            </ErrorBoundary>
          </div>
        </div>
      </FDMigrationGate>
    </div>
  );
};

ProtectedLayout.propTypes = {
  isAuthenticated: PropTypes.bool,
  sessionExpireAfter: PropTypes.number,
  organizationName: PropTypes.string,
};

// Authenticated but chrome-less layout: no SideNav / Topbar, just the routed view.
// Used for the rule-explainability page so it renders cleanly inside an iframe.
const RuleExplainabilityLayout = ({ isAuthenticated }) => {
  const location = useLocation();

  if (!isAuthenticated) {
    const nextFullPath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    const destination = `${SPA_PREFIX}/login${nextFullPath ? `?next=${encodeURIComponent(nextFullPath)}` : ''}`;
    return <Navigate to={destination} state={{ from: location }} replace />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingView />}>
        <Outlet />
      </Suspense>
    </ErrorBoundary>
  );
};

RuleExplainabilityLayout.propTypes = {
  isAuthenticated: PropTypes.bool,
};

// React Router v6 doesn't support `render` in its <Route> component, meaning we don't get the match object
// by default there. There's a way to replace the functionality with the `element` property on v6's <Route> component,
// but we can't use that property with the compatibility library. This component seeks to mimic the behavior with the <Route>-
// provided `match` while still allowing for an incremental migration
type RouteWithMatchWrapperProps = { currentRoute: string };
const RouteWithMatchWrapper = (
  { currentRoute }: RouteWithMatchWrapperProps,
) => {
  const match = useMatch(currentRoute);
  return match ? <WorkflowEditorView /> : <NoMatchView />;
};

type RouteWithSeachAndMatchWrapperProps = {
  currentRoute: string;
  pathParam: 'customerId' | 'entityGroupToken';
};

const RouteWithSeachAndMatchWrapper = (
  { currentRoute, pathParam }: RouteWithSeachAndMatchWrapperProps) => {
  const match = useMatch(currentRoute);
  const { search } = useLocation();

  if (!match) {
    return <NoMatchView />;
  }

  const { params } = match;

  if (pathParam === 'customerId') {
    return <Navigate replace to={`${SPA_PREFIX}/admin/customers/${params[pathParam]}${search || ''}`} />;
  }
  return currentRoute.includes('review')
    ? (
      <Navigate
        replace
        to={`${SPA_PREFIX}/groups/${params[pathParam]}/review${search || ''}`}
      />
    )
    : (
      <Navigate
        replace
        to={`${SPA_PREFIX}/groups/${params[pathParam]}${search || ''}`}
      />
    );
};

const SARRoute = ({ childComponent }) => {
  const {
    agent: { isEmployee },
    featureFlags: { sarReport: sarFlag, reportingPermissions },
    permissions: {
      'READ:SarReports:null': sarPermission,
      'READ:SarReports:sar_report_id': sarReadPermission,
    },
  } = useContext(UserContext);
  const { caseToken = '', investigationToken = '' } = useParams();
  const unifiedSarPermission = reportingPermissions ? sarReadPermission : sarPermission;
  const canAccessSAR = sarFlag && Boolean(unifiedSarPermission) && !isEmployee;

  return canAccessSAR ? childComponent : <Navigate replace to={investigationToken ? `/investigations/${investigationToken}` : `${SPA_PREFIX}/cases/${caseToken}`} />;
};

SARRoute.propTypes = {
  childComponent: PropTypes.element,
};

const Dashboard = ({
  setPrintMode, redirectToAlloyCom, cellBaseAppUrls, isEphemeralEnvironment,
}) => {
  const { resetToDefault } = useTitleContext();
  const { isAuthenticated } = useContext(AuthContext);

  const context = useContext(UserContext);
  const {
    agent: { email, isEmployee, isAfefParentAgent },
    customer: { organizationNameSlug, organizationName, vanityDomainSlug },
    featureFlags: {
      caseManagement,
      currencyTransactionReports,
      journeys,
      journeyEditorV2,
      portfolioEvaluations,
      usePendo,
      sarsListView,
      redirectToVanity,
      enablePartnerCenterV2,
      enableSelfServeAttribute,
      enableAiOverview,
    },
    adminCustomerAccess,
    sessionExpireAfter,
    adminPermissions,
    permissions,
  } = context;

  const currentDomain = window.location.hostname;
  const currentPath = window.location.pathname + window.location.search + window.location.hash;

  const cellBaseAppUrl = cellBaseAppUrls.find((url: string) => url.endsWith('.com'));
  if (cellBaseAppUrl) {
    const cellBaseAppUrlWithoutProtocol = cellBaseAppUrl.replace('https://', '').replace('http://', '');

    /*
    If a customer has a vanityDomainSlug (as returned in the response of the UserContext API)
    redirection logic should use the vanityDomainSlug instead of the organizationNameSlug
    to redirect to the correct URL.
    */
    const customerVanityDomain = vanityDomainSlug || organizationNameSlug;

    if (currentDomain.endsWith('.alloy.co') && redirectToAlloyCom) {
      if (!isAuthenticated) {
        window.location.href = `${cellBaseAppUrl}${currentPath}`;
        return null;
      }
      if (redirectToVanity && customerVanityDomain && (!currentPath.includes('/admin') || isAfefParentAgent)) {
        window.location.href = `https://${customerVanityDomain}.app.alloy.com${currentPath}`;
      } else {
        window.location.href = `${cellBaseAppUrl}${currentPath}`;
      }
      return null;
    }

    if (
      currentDomain === cellBaseAppUrlWithoutProtocol
      && customerVanityDomain
      // this check protects against the case where the customer has a vanity domain that is the same as the cell base app url
      && cellBaseAppUrlWithoutProtocol !== `${customerVanityDomain}.app.alloy.com`
      && redirectToAlloyCom
      && redirectToVanity
      && isAuthenticated // without this customerVanityDomain is undefined
      && (!currentPath.includes('/admin') || isAfefParentAgent)
    ) {
      // Create a URL object from the target URL
      const targetUrl = new URL(`https://${customerVanityDomain}.app.alloy.com${currentPath}`);

      // Append the new query parameter
      targetUrl.searchParams.append('vanityDomainRedirect', 'true');

      // Navigate to the final URL
      window.location.href = targetUrl.toString();
      return null;
    }

    // This block exists to redirect to the cell base app url for the provisioning dashboard even when vanity_url query param is defined
    if (
      currentDomain !== cellBaseAppUrlWithoutProtocol
      && redirectToAlloyCom
      && redirectToVanity
      && isAuthenticated // without this customerVanityDomain is undefined
      && customerVanityDomain
      && currentPath.includes('/admin')
      && !isAfefParentAgent
    ) {
      // Create a URL object from the target URL
      const targetUrl = new URL(`https://${cellBaseAppUrlWithoutProtocol}${currentPath}`);

      // Navigate to the final URL
      window.location.href = targetUrl.toString();
      return null;
    }
  }

  // TODO: is this kosher?
  if (isAuthenticated && isEmployee === undefined) {
    return <LoadingView />;
  }

  useEffect(() => {
    const handleBeforePrint = () => setPrintMode(true);
    const handleAfterPrint = () => setPrintMode(false);
    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (usePendo && email) {
      // Can view workflows and workflow versions
      const canViewWorkflows = Boolean(permissions['READ:Applications:application_id']
        && permissions['READ:ApplicationVersions:null']);
      // Can view and create workflows and workflow versions
      const canCreateWorkflows = Boolean(canViewWorkflows && permissions['CREATE:Applications:null']
        && permissions['CREATE:ApplicationVersions:null']);
      // Can view workflows and workflow versions and create workflow versions - i.e. edit workflows
      const canEditWorkflows = Boolean(canViewWorkflows && permissions['CREATE:ApplicationVersions:null']);
      // @ts-expect-error apparently you don't need to initialize pendo in this file for it to work?
      pendo.initialize({
        excludeAllText: true,
        visitor: {
          id: email,
          email,
          slug: organizationNameSlug,
          isEmployee,
          canViewWorkflows,
          canCreateWorkflows,
          canEditWorkflows,
        },
        account: {
          id: organizationNameSlug,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usePendo, email]);

  const customerAccessRecord = find(adminCustomerAccess, { organizationNameSlug });
  const currentTime = new Date();

  /* user should have access to the dashboard if either they have permenent or temporary access. Permanent access is recorded as is_permenent. The check for temporary access is if the organization_name_slug is in the admin_customer_access object AND its expired_at property is neither null nor earlier than the current date */
  const userHasAccessToDashboard = (!isEmployee || adminPermissions['READ:Customers:null'])
    || (!!customerAccessRecord && (
      customerAccessRecord.isPermanent || (
        customerAccessRecord.expiredAt !== null
        && currentTime.getTime() < new Date(customerAccessRecord.expiredAt).getTime()
      )
    ));

  useEffect(() => {
    if (!userHasAccessToDashboard) {
      resetToDefault();
    }
  }, [resetToDefault, userHasAccessToDashboard]);

  const isPortfolioEvaluationsEnabled = !!portfolioEvaluations;

  // const { deleteMessage, displayedAt, messages } = useContext(MessageContext);

  const canViewPartnerCenter = (
    enablePartnerCenterV2 && permissions['READ:Services:null']
  );
  const canViewPSIntegrations = (
    canViewPartnerCenter && permissions['READ:ServiceProducts:null']
  );

  // TODO: Remove isEmployee and orgCanViewAttribute when we enable the attribute tab.
  // https://team-alloy.atlassian.net/browse/DPI-3336
  const canViewPSAttributes = (
    (isEmployee || enableSelfServeAttribute) && canViewPSIntegrations && permissions['READ:Attributes:null']
  );
  const canCreateUpdatePSAttributes = (
    canViewPSAttributes && permissions['UPDATE:Attributes:null']
  );

  const getPartnerCenterView = () => {
    if (!canViewPartnerCenter) {
      return <Navigate replace to={`${SPA_PREFIX}/error/401/`} />;
    }
    return <PartnerCenterView />;
  };

  const canViewAiOverview = Boolean(enableAiOverview) && userHasAccessToDashboard;
  const getAIOverviewView = () => {
    if (!canViewAiOverview) {
      return <Navigate replace to={`${SPA_PREFIX}/error/401/`} />;
    }
    return <AIOverviewView />;
  };

  return (
    <>
      <Helmet>
        <link rel="icon" type="image/png" href={favicon} sizes="16x16" />
      </Helmet>
      <Router>
        <Routes>
          <Route path={`${SPA_PREFIX}/error/:statusCode/`} element={<ErrorStatusView />} />
          {/* Chrome-less, evaluation-anchored read-only rule explainability (iframe target) */}
          <Route element={<RuleExplainabilityLayout isAuthenticated={isAuthenticated} />}>
            <Route
              path={`${SPA_PREFIX}/entities/:entityToken/evaluations/:evaluationToken/explainability`}
              element={<RuleExplainabilityView />}
            />
          </Route>
          <Route element={<PublicOnlyLayout isAuthenticated={isAuthenticated} redirectToVanity={redirectToVanity} redirectToAlloyCom={redirectToAlloyCom} />}>
            <Route path={`${SPA_PREFIX}`} element={<AuthContainerView />}>
              {/* ${SPA_PREFIX}/login */}
              <Route path="login" element={<SigninView />} />
              {/* ${SPA_PREFIX}/login/admin */}
              <Route path="login/admin" element={<SigninView />} />
              {/* ${SPA_PREFIX}/mfa/:tokenUid/:token */}
              <Route path="mfa/:tokenUid/:token" element={<TotpVerifyView />} />
              {/* ${SPA_PREFIX}/mfa/verify/:tokenUid/:token */}
              <Route path="mfa/verify/:tokenUid/:token" element={<MFASetupView />} />
              {/* ${SPA_PREFIX}/mfa/totp/:tokenUid/:token */}
              <Route path="mfa/totp/:tokenUid/:token" element={<TotpVerifyView />} />
              {/* ${SPA_PREFIX}/mfa/totp/verify/:tokenUid/:token */}
              <Route path="mfa/totp/verify/:tokenUid/:token" element={<MfaSetupViewWithTotp />} />
              {/* ${SPA_PREFIX}/forgot-password */}
              <Route path="forgot-password" element={<ForgotPasswordView />} />
              {/* ${SPA_PREFIX}/reset-password/:tokenUid/:token */}
              <Route path="reset-password/:tokenUid/:token" element={<ResetPasswordView mode={ResetPasswordViewMode.RESETPW} />} />
              {/* ${SPA_PREFIX}/invitation/:tokenUid/:token */}
              <Route path="invitation/:tokenUid/:token" element={<ResetPasswordView mode={ResetPasswordViewMode.SETPW} />} />
              {/* ${SPA_PREFIX}/verify-email/:tokenUid/:token */}
              <Route path="verify-email/:tokenUid/:token" element={<VerifyEmailView mode={ValidateEmailMode.VALIDATEEMAIL} />} />
              {/* ${SPA_PREFIX}/verify-customer-email/:tokenUid/:tokenCid/:token */}
              <Route path="verify-customer-email/:tokenUid/:tokenCid/:token" element={<VerifyEmailView mode={ValidateEmailMode.VALIDATECUSTOMEREMAIL} />} />
            </Route>
          </Route>
          <Route element={<ProtectedLayout isAuthenticated={isAuthenticated} sessionExpireAfter={sessionExpireAfter} organizationName={organizationName} />}>
            {/* ${SPA_PREFIX} */}
            <Route path={`${SPA_PREFIX}`}>
              {/* ${SPA_PREFIX}/admin/customers -- only putting this here rather than under SpaAdminPassThrough because of isAfefParentAgent condition */}
              <Route
                path="admin/customers"
                element={isAfefParentAgent || isEmployee
                  ? (
                    <CustomerListViewProvisioning
                      isEphemeralEnvironment={isEphemeralEnvironment}
                      cellBaseAppUrls={cellBaseAppUrls}
                    />
                  )
                  : <Navigate replace to={`${SPA_PREFIX}/settings`} />}
              />
              {/* ${SPA_PREFIX}/admin */}
              <Route path="admin" element={<SpaAdminPassThrough isEmployee={isEmployee} />}>
                <Route index element={<Navigate replace to={`${SPA_PREFIX}/admin/customers/`} />} />
                {/* ${SPA_PREFIX}/admin/chimera_decrypt */}
                <Route path="chimera_decrypt" element={<Navigate replace to={`${SPA_PREFIX}/admin/decrypt`} />} />
                {/* ${SPA_PREFIX}/admin/ai */}
                <Route path="ai" element={<AiIndex />} />
                {/* ${SPA_PREFIX}/admin/customers/:customerId */}
                <Route path="customers/:customerId" element={<CustomersDetailView />} />
                {/* ${SPA_PREFIX}/admin/employees */}
                <Route path="employees" element={<EmployeesIndex />} />
                {/* ${SPA_PREFIX}/admin/metrics */}
                <Route path="metrics" element={<Metrics />} />
                {/* ${SPA_PREFIX}/admin/customer-access */}
                <Route path="customer-access" element={<CustomerAccessRequestDetails />} />
                {/* ${SPA_PREFIX}/admin/customer-access/:requestId */}
                <Route path="customer-access/:requestId" element={<CustomerAccessRequestDetails />} />
                {/* ${SPA_PREFIX}/admin/decrypt */}
                <Route path="decrypt/:service/:year/:month/:day/:hour/:minute/:second/:logType/:encryptedLogId" element={<DecryptLogsView />} />
                <Route path="decrypt/:service/:year/:month/:day/:hour/:minute/:logType/:encryptedLogId" element={<DecryptLogsView />} />
                <Route path="decrypt/:service/:year/:month/:day/:hour/:logType/:encryptedLogId" element={<DecryptLogsView />} />
                {/* ${SPA_PREFIX}/admin/decrypt/logs */}
                <Route path="decrypt/logs" element={<DecryptLogsViewOld />} />
                {/* ${SPA_PREFIX}/admin/global-services */}
                <Route path="global-services" element={<Services />} />
                {/* ${SPA_PREFIX}/admin/global-services/:internalName */}
                <Route path="global-services/:internalName" element={<ServiceDetail />} />
                {/* ${SPA_PREFIX}/admin/global-services/:internalName/attribute/:attributeToken */}
                <Route path="global-services/:internalName/attribute/:attributeToken" element={<ServiceAttributeDetailsView />} />
                {/* ${SPA_PREFIX}/admin/global-services/:internalName/attribute/create */}
                <Route path="global-services/:internalName/attribute/create" element={<ServiceAttributeCreationOverlay mode="create" />} />
                {/* ${SPA_PREFIX}/admin/global-services/:internalName/attribute/duplicate */}
                <Route path="global-services/:internalName/attribute/duplicate" element={<ServiceAttributeCreationOverlay mode="duplicate" />} />
                {/* ${SPA_PREFIX}/admin/library/snippets */}
                <Route path="library/snippets" element={<GlobalRepository />} />
                {/* ${SPA_PREFIX}/admin/library/snippets/:snippetToken/versions/:versionId/update */}
                <Route
                  path="library/snippets/:snippetToken/versions/:versionId/update"
                  element={isEmployee
                    ? (
                      <LibraryRouteGuard>
                        <WorkflowEditorView />
                      </LibraryRouteGuard>
                    )
                    : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />}
                />
                {/* ${SPA_PREFIX}/admin/library/snippets/:snippetToken/versions/create */}
                <Route
                  path="library/snippets/:snippetToken/versions/create"
                  element={isEmployee
                    ? (
                      <LibraryRouteGuard>
                        <WorkflowEditorView />
                      </LibraryRouteGuard>
                    )
                    : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />}
                />
                {/* ${SPA_PREFIX}/admin/library/snippets/:snippetToken */}
                <Route
                  path="library/snippets/:snippetToken"
                  element={isEmployee ? <GlobalSnippetDetailsView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />}
                />
                {/* Back-compat: redirect old /admin/library/global-repository* to /admin/library/snippets* */}
                <Route path="library/global-repository/*" element={<RedirectGlobalRepositoryToSnippets />} />
                {/* ${SPA_PREFIX}/admin/global-services/:internalName/attribute/update */}
                <Route path="global-services/:internalName/attribute/update" element={<ServiceAttributeCreationOverlay mode="update" />} />
                {/* ${SPA_PREFIX}/admin/notifications/ */}
                <Route path="notifications" element={<NotificationListView />} />
                {/* ${SPA_PREFIX}/admin/request-access/ */}
                <Route path="request-access" element={<RequestAccessView />} />
                {/* ${SPA_PREFIX}/admin/request-access/:customerId */}
                <Route path="request-access/:customerId" element={<RequestAccessView />} />
                {/* ${SPA_PREFIX}/admin/reindex */}
                <Route path="reindex" element={<ReindexView />} />
                {/* ${SPA_PREFIX}/admin/services */}
                <Route path="services" element={<Navigate replace to={`${SPA_PREFIX}/admin/global-services/`} />} />
                {/* end ${SPA_PREFIX}/admin */}
              </Route>
              {/* ${SPA_PREFIX}/audit */}
              <Route path="audit" element={userHasAccessToDashboard ? <AuditView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/approvals */}
              <Route path="approvals" element={userHasAccessToDashboard ? <ApprovalTableView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/cases */}
              <Route path="cases" element={caseManagement && userHasAccessToDashboard ? <CasesView /> : <NoMatchView />} />
              {/* ${SPA_PREFIX}/cases/:caseToken */}
              <Route path="cases/:caseToken" element={caseManagement && userHasAccessToDashboard ? <CaseView /> : <NoMatchView />} />
              {/* ${SPA_PREFIX}/cases/:caseToken/report */}
              <Route path="cases/:caseToken/report" />
              {/* ${SPA_PREFIX}/cases/:caseToken/report/:reportToken/:reportVersion? */}
              <Route path="cases/:caseToken/report/:reportToken/:reportVersion?" element={userHasAccessToDashboard ? <SARRoute childComponent={<SARForm />} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/cases/:caseToken/report/new */}
              <Route path="cases/:caseToken/report/new" element={userHasAccessToDashboard ? <SARRoute childComponent={<SARForm />} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/ctrs */}
              <Route path="ctrs" element={currencyTransactionReports && userHasAccessToDashboard ? <CTRListView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/ctrs/:reportToken */}
              <Route path="ctrs/:reportToken" element={currencyTransactionReports && userHasAccessToDashboard ? <CTRFormView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/ctrs/new */}
              <Route path="ctrs/new" element={currencyTransactionReports && userHasAccessToDashboard ? <CTRFormView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/entities/:entityToken/portfolio-evaluations/:portfolioEvaluationToken */}
              <Route path="entities/:entityToken/portfolio-evaluations/:portfolioEvaluationToken" element={isPortfolioEvaluationsEnabled && userHasAccessToDashboard ? <PortfolioEvaluationView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/evaluations */}
              <Route path="evaluations" element={userHasAccessToDashboard ? <EvaluationListView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/evaluations/run */}
              <Route path="evaluations/run" element={userHasAccessToDashboard ? <EvaluationRunViewWrapper /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/groups/:entityGroupToken */}
              <Route path="groups/:entityGroupToken/" element={userHasAccessToDashboard ? <GroupDetailView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/groups/:entityGroupToken/review */}
              <Route path="groups/:entityGroupToken/review" element={userHasAccessToDashboard ? <GroupDetailView isReview /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/home/* */}
              <Route path="home/*" element={userHasAccessToDashboard ? <HomeDashboard /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/journeys */}
              <Route path="journeys/*" element={journeys && userHasAccessToDashboard ? <JourneyRoutes /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/policy/:journeyToken/versions/:versionId */}
              <Route path="policy/:journeyToken/versions/:versionId" element={journeys && userHasAccessToDashboard && journeyEditorV2 ? <PolicyEditorView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/notifications */}
              <Route path="notifications" element={userHasAccessToDashboard ? <NotificationsView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/partner-solutions/:serviceInternalName/attributes/:attributeId/duplicate */}
              <Route path="partner-solutions/:serviceInternalName/attributes/:attributeId/duplicate" element={canCreateUpdatePSAttributes ? <AttributeCreation mode="duplicate" level="customer" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/partner-solutions/:serviceInternalName/attributes/:attributeId/update */}
              <Route path="partner-solutions/:serviceInternalName/attributes/:attributeId/update" element={canCreateUpdatePSAttributes ? <AttributeCreation mode="update" level="customer" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/partner-solutions/:serviceInternalName/attributes/create */}
              <Route path="partner-solutions/:serviceInternalName/attributes/create" element={canCreateUpdatePSAttributes ? <AttributeCreation mode="create" level="customer" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/partner-solutions */}
              <Route path="partner-solutions" element={getPartnerCenterView()} />
              {/* ${SPA_PREFIX}/ai-assistant */}
              <Route path="ai-assistant" element={getAIOverviewView()} />
              {/* ${SPA_PREFIX}/ai-assistant/agents/:agentKey — agent detail page. AgentDetailView reads
                  agentKey from the URL itself and renders a not-found state for an unrecognized one. */}
              <Route path="ai-assistant/agents/:agentKey" element={canViewAiOverview ? <AgentDetailView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* {`${SPA_PREFIX}/partner-solutions/:serviceInternalName/attributes/:attributeId`} */}
              <Route path="partner-solutions/:serviceInternalName/attributes/:attributeId" element={canViewPSAttributes ? <CustomerAttributeDetailsView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/partner-solutions/:serviceInternalName */}
              <Route path="partner-solutions/:serviceInternalName" element={canViewPSIntegrations ? <IntegrationView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/portfolio-evaluations/:portfolioEvaluationToken */}
              <Route path="portfolio-evaluations/:portfolioEvaluationToken" element={isPortfolioEvaluationsEnabled && userHasAccessToDashboard ? <PortfolioEvaluation /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/portfolio */}
              <Route path="portfolio-evaluations" element={userHasAccessToDashboard ? <EvaluationListView openTab={EvaluationsTabs.PORTFOLIO} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/post-login/close */}
              <Route path="post-login/close" element={<PostLoginCloseView />} />
              {/* ${SPA_PREFIX}/queues */}
              <Route path="queues" element={userHasAccessToDashboard ? <ReviewQueueView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/reports/:bsaId */}
              <Route path="reports/:bsaId" element={userHasAccessToDashboard ? <SARRoute childComponent={<SARForm />} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/settings */}
              <Route path="settings/*" element={userHasAccessToDashboard ? <SettingsView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/workflows */}
              <Route path="workflows" element={userHasAccessToDashboard ? <WorkflowListContainer /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/workflows/:workflowToken */}
              <Route path="workflows/:workflowToken" element={userHasAccessToDashboard ? <WorkflowDetailsView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/workflows/:workflowToken/what-if/:analysisToken/evaluations/:evaluationToken */}
              <Route path="workflows/:workflowToken/what-if/:analysisToken/evaluations/:evaluationToken" element={userHasAccessToDashboard ? <WhatIfEvalutionView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/workflows/:workflowToken/:testType/:testToken/completed-results */}
              <Route path="workflows/:workflowToken/:testType/:testToken/completed-results" element={userHasAccessToDashboard ? <TestHistoryResultsPage /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/workflows/:workflowToken/:testType/:testToken */}
              <Route path="workflows/:workflowToken/:testType/:testToken" element={userHasAccessToDashboard ? <TestHistoryResultsPage /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/ai-testing (landing) + /new (create+run) + /:workflowToken/:testToken (a run's results) — AI section, gated on enableAiOverview */}
              <Route path="ai-testing" element={canViewAiOverview ? <AiTestingResultsPage /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              <Route path="ai-testing/new" element={canViewAiOverview ? <AiTestingBacktestSetup /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              <Route path="ai-testing/:workflowToken/:testToken" element={canViewAiOverview ? <AiTestingResultsPage /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/workflows/:workflowToken/versions/:versionId/update */}
              <Route path="workflows/:workflowToken/versions/:versionId/update" element={userHasAccessToDashboard ? <RouteWithMatchWrapper currentRoute={`${SPA_PREFIX}/workflows/:workflowToken/versions/:versionId/update`} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/workflows/:workflowToken/versions/create */}
              <Route path="workflows/:workflowToken/versions/create" element={userHasAccessToDashboard ? <RouteWithMatchWrapper currentRoute={`${SPA_PREFIX}/workflows/:workflowToken/versions/create`} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/workflows/:workflowToken/create */}
              <Route path="workflows/:workflowToken/create" element={userHasAccessToDashboard ? <RouteWithMatchWrapper currentRoute={`${SPA_PREFIX}/workflows/:workflowToken/create`} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* ${SPA_PREFIX}/snippets/:snippetToken/versions/:versionId/update */}
              <Route
                path="snippets/:snippetToken/versions/:versionId/update"
                element={userHasAccessToDashboard ? <WorkflowEditorView scope="local" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />}
              />
              {/* ${SPA_PREFIX}/snippets/:snippetToken/versions/create */}
              <Route
                path="snippets/:snippetToken/versions/create"
                element={userHasAccessToDashboard ? <WorkflowEditorView scope="local" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />}
              />
              {/* ${SPA_PREFIX}/snippets/:snippetToken */}
              <Route path="snippets/:snippetToken" element={userHasAccessToDashboard ? <LocalSnippetDetailsView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
              {/* end ${SPA_PREFIX} */}
            </Route>
            {/* /admin/customers */}
            <Route path="admin/customers" element={<Navigate replace to={`${SPA_PREFIX}/admin/customers`} />} />
            {/* /admin/customers/:customerId */}
            <Route path="admin/customers/:customerId" element={<RouteWithSeachAndMatchWrapper currentRoute="/admin/customers/:customerId" pathParam="customerId" />} />
            {/* /admin/notifications */}
            <Route path="admin/notifications" element={<Navigate to={`${SPA_PREFIX}/admin/notifications`} replace />} />
            {/* /admin/services */}
            <Route path="admin/services" element={<Navigate replace to={`${SPA_PREFIX}/admin/global-services/`} />} />
            {/* /admin/services/:service */}
            <Route path="admin/services/:service" element={<RouteWithServiceParamWrapper currentRoute="/admin/services/:service" />} />
            {/* /applications */}
            <Route path="applications" element={(<Navigate replace to={userHasAccessToDashboard ? routes.v3.dashboard.workflows.list : `${SPA_PREFIX}/error/401/`} />)} />
            {/* /approvals */}
            <Route path="approvals" element={(<Navigate replace to={userHasAccessToDashboard ? `${SPA_PREFIX}/approvals` : `${SPA_PREFIX}/error/401/`} />)} />
            {/* /evaluations */}
            <Route path="evaluations" element={userHasAccessToDashboard ? <EvaluationListView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /evaluations/run */}
            <Route path="evaluations/run" element={<Navigate to={userHasAccessToDashboard ? `${SPA_PREFIX}/evaluations/run` : `${SPA_PREFIX}/error/401/`} />} />
            {/* /entities */}
            <Route path="entities" element={userHasAccessToDashboard ? <EntitiesListView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /entities/:entityToken */}
            <Route path="entities/:entityToken" element={userHasAccessToDashboard ? <EntityDetailView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /entities/:entityToken/:applications/:applicationToken/review" */}
            <Route path="entities/:entityToken/:applications/:applicationToken/review" element={userHasAccessToDashboard ? <EntityReviewView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /entities/:entityToken/evaluations/:evaluationToken */}
            <Route path="entities/:entityToken/evaluations/:evaluationToken" element={userHasAccessToDashboard ? <EntityEventView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /entities/:entityToken/evaluations/:evaluationToken/rerun */}
            <Route path="entities/:entityToken/evaluations/:evaluationToken/rerun" element={userHasAccessToDashboard ? <RerunEvaluationView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /entities/:entityToken/reviews/:reviewToken */}
            <Route path="entities/:entityToken/reviews/:reviewToken" element={userHasAccessToDashboard ? <EntityEventView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /groups/:entityGroupToken */}
            <Route path="groups/:entityGroupToken" element={userHasAccessToDashboard ? <RouteWithSeachAndMatchWrapper currentRoute="/groups/:entityGroupToken" pathParam="entityGroupToken" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /groups/:entityGroupToken/review */}
            <Route path="groups/:entityGroupToken/review" element={userHasAccessToDashboard ? <RouteWithSeachAndMatchWrapper currentRoute="/groups/:entityGroupToken/review" pathParam="entityGroupToken" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /investigations */}
            <Route path="investigations/" element={userHasAccessToDashboard ? <InvestigationsQueueView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /investigations/:investigationToken */}
            <Route path="investigations/:investigationToken" element={userHasAccessToDashboard ? <InvestigationDetailView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /investigations/:investigationToken/report/new */}
            <Route path="investigations/:investigationToken/report/new" element={userHasAccessToDashboard ? <SARRoute childComponent={<SARForm />} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /investigations/:investigationToken/report/:reportToken/:reportVersion? */}
            <Route path="investigations/:investigationToken/report/:reportToken/:reportVersion?" element={userHasAccessToDashboard ? <SARRoute childComponent={<SARForm />} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /portfolio-evaluations */}
            <Route path="portfolio-evaluations" element={userHasAccessToDashboard ? <EvaluationListView openTab={EvaluationsTabs.PORTFOLIO} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /sars */}
            <Route path="sars" element={userHasAccessToDashboard && sarsListView ? <SARRoute childComponent={<SARListView />} /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /queues */}
            <Route path="queues" element={userHasAccessToDashboard ? <ReviewQueueView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /workflows */}
            <Route path="workflows" element={userHasAccessToDashboard ? <WorkflowListContainer /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /workflows/:workflowToken */}
            <Route path="workflows/:workflowToken" element={userHasAccessToDashboard ? <WorkflowDetailsView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /workflows/:workflowToken/what-if/:analysisToken/evaluations/:evaluationToken */}
            <Route path="workflows/:workflowToken/what-if/:analysisToken/evaluations/:evaluationToken" element={userHasAccessToDashboard ? <WhatIfEvalutionView /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /workflows/:workflowToken/:testType/:testToken/completed-results */}
            <Route path="workflows/:workflowToken/:testType/:testToken/completed-results" element={userHasAccessToDashboard ? <TestHistoryResultsPage /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /workflows/:workflowToken/:testType/:testToken */}
            <Route path="workflows/:workflowToken/:testType/:testToken" element={userHasAccessToDashboard ? <TestHistoryResultsPage /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /ai-testing (landing) + /new (create+run) + /:workflowToken/:testToken (a run's results) — AI section, gated on enableAiOverview */}
            <Route path="ai-testing" element={canViewAiOverview ? <AiTestingResultsPage /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            <Route path="ai-testing/new" element={canViewAiOverview ? <AiTestingBacktestSetup /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            <Route path="ai-testing/:workflowToken/:testToken" element={canViewAiOverview ? <AiTestingResultsPage /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /workflows/:workflowToken/versions/:versionId/update */}
            <Route path="workflows/:workflowToken/versions/:versionId/update" element={userHasAccessToDashboard ? <RouteWithMatchWrapper currentRoute="/workflows/:workflowToken/versions/:versionId/update" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /workflows/:workflowToken/versions/:versionId/create */}
            <Route path="workflows/:workflowToken/versions/create" element={userHasAccessToDashboard ? <RouteWithMatchWrapper currentRoute="/workflows/:workflowToken/versions/create" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /workflows/:workflowToken/create */}
            <Route path="workflows/:workflowToken/create" element={userHasAccessToDashboard ? <RouteWithMatchWrapper currentRoute="/workflows/:workflowToken/create" /> : <Navigate replace to={`${SPA_PREFIX}/error/401/`} />} />
            {/* /applications/:workflowTokens/versions/create */}
            <Route path="applications/:workflowToken/versions/create" element={<RouteWithMatchWrapper currentRoute="/applications/:workflowToken/versions/create" />} />
          </Route>
          <Route path="*" element={<NoMatchView />} />
        </Routes>
      </Router>
    </>
  );
};

Dashboard.propTypes = {
  setPrintMode: PropTypes.func.isRequired,
  redirectToAlloyCom: PropTypes.bool,
  cellBaseAppUrls: PropTypes.arrayOf(PropTypes.string),
  isEphemeralEnvironment: PropTypes.bool,
};

export default connect(null, {
  setPrintMode: _setPrintMode,
})(Dashboard);
